import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { Plus, ArrowRight, Inbox, PlayCircle, CheckCircle2, TrendingUp } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-zinc-100 text-zinc-700 border-zinc-200",
  running: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-semibold uppercase tracking-widest ${cls}`}
    >
      {status === "running" && (
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
      )}
      {status}
    </span>
  );
}

function MetricCard({ label, value, icon: Icon, testid }) {
  return (
    <div
      className="bg-white border border-zinc-200 rounded-md p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{label}</span>
        <Icon className="w-4 h-4 text-zinc-400" />
      </div>
      <div className="font-extrabold text-3xl tracking-tight" data-testid={`${testid}-value`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [migrations, setMigrations] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, completed: 0, success_rate: 0, messages_moved: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [migsRes, statsRes] = await Promise.all([
        api.get("/migrations"),
        api.get("/migrations/stats"),
      ]);
      setMigrations(migsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="p-8 lg:p-12 max-w-7xl">
      <div className="flex items-start justify-between mb-10">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Control Room</p>
          <h1 className="text-4xl font-extrabold tracking-tight">Dashboard</h1>
        </div>
        <Link
          to="/migrations/new"
          data-testid="new-migration-cta"
          className="inline-flex items-center gap-2 bg-[#FF4F00] hover:bg-[#CC3F00] text-white font-semibold px-5 py-2.5 rounded-md transition-all active:scale-95"
        >
          <Plus className="w-4 h-4" />
          New migration
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <MetricCard label="Total jobs" value={stats.total} icon={Inbox} testid="metric-total" />
        <MetricCard label="Running" value={stats.active} icon={PlayCircle} testid="metric-active" />
        <MetricCard label="Completed" value={stats.completed} icon={CheckCircle2} testid="metric-completed" />
        <MetricCard label="Success rate" value={`${stats.success_rate}%`} icon={TrendingUp} testid="metric-success-rate" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-lg">Migration jobs</h2>
            <p className="text-xs font-mono text-zinc-500 mt-0.5">
              Messages moved lifetime: <span className="text-zinc-900 font-semibold">{stats.messages_moved.toLocaleString()}</span>
            </p>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-sm font-mono text-zinc-500">Loading…</div>
        ) : migrations.length === 0 ? (
          <div className="p-16 text-center" data-testid="empty-migrations">
            <div className="w-12 h-12 rounded-md bg-zinc-100 flex items-center justify-center mx-auto mb-4">
              <Inbox className="w-5 h-5 text-zinc-500" />
            </div>
            <h3 className="font-bold text-xl mb-2">No migrations yet</h3>
            <p className="text-zinc-600 text-sm mb-6">Set up your first mailbox migration in seconds.</p>
            <Link
              to="/migrations/new"
              data-testid="empty-cta"
              className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white font-semibold px-5 py-2.5 rounded-md transition-all"
            >
              Create migration <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <table className="w-full" data-testid="migrations-table">
            <thead>
              <tr className="text-left text-xs font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Source → Destination</th>
                <th className="px-6 py-3">Progress</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {migrations.map((m) => {
                const total = m.progress?.total_messages || 0;
                const done = m.progress?.processed_messages || 0;
                const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
                return (
                  <tr key={m.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 transition-colors" data-testid={`migration-row-${m.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-semibold">{m.name}</div>
                      <div className="text-xs font-mono text-zinc-500 mt-0.5">{m.id.slice(0, 8)}</div>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs">
                      <div className="text-zinc-800">{m.source.email}</div>
                      <div className="text-zinc-400">↓</div>
                      <div className="text-zinc-800">{m.destination.email}</div>
                    </td>
                    <td className="px-6 py-4 min-w-[180px]">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#FF4F00] transition-all duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs font-mono text-zinc-600 tabular-nums w-14 text-right">
                          {done}/{total || "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusPill status={m.status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/migrations/${m.id}`}
                        data-testid={`view-migration-${m.id}`}
                        className="text-sm font-semibold text-zinc-950 hover:text-[#FF4F00] transition-colors"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
